import { METRIC_TYPES } from "helpers/metrics/constants"
import { FACTOR_SENSITIVITY_TYPES } from "helpers/factorSensitivity"
import { createExposureDrilldownGatherers } from "gatherers/contributors/drilldown-exposure"
import { createRiskDrilldownGatherers } from "gatherers/contributors/drilldown-risk"
import { createFactorSensitivityDrilldownGatherers } from "gatherers/contributors/drilldown-factor-sensitivity"
import { createBetaDrilldownGatherers } from "gatherers/contributors/drilldown-beta"
import { createPerformanceDrilldownGatherers } from "gatherers/contributors/drilldown-performance"
import { createContentSetDrilldownGatherers } from "gatherers/contributors/drilldown-content-set"
import { createDrilldownBaseGatherers } from "gatherers/contributors/drilldown-base"
import { computeScopedBaseSegment, parseFilterScope } from "helpers/filter-scope"
import { computeAttributionMethod } from "helpers/risk"
import { resolveDrilldownInstruments } from "connected-components/dashboard/drilldown/drilldown-instrument-series"
import type { DrilldownFrozenSnapshot, DrilldownSeed } from "connected-components/dashboard/drilldown/types"

export const NAMED_VIEW_LABELS: Record<string, string> = {
  performance: "Performance",
  risk: "Risk",
  exposure: "Exposure",
  beta: "Beta",
  "factor-sensitivity": "Predicted Factor Sensitivity",
  predictedFactorSensitivity: "Predicted Factor Sensitivity",
  historicalFactorSensitivity: "Historical Factor Sensitivity",
  composition: "Composition",
}

// The per-instrument option bag is assembled dynamically and forwarded to the view-specific
// gatherer factories, which each accept their own loosely-typed option shape. Typing it precisely
// would mean unifying all factory signatures — out of scope here; the public entry point below is
// what's strongly typed.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GathererOptions = any

function createGatherersForInstrument(view: string, options: GathererOptions) {
  // risk & performance show factor exposure alongside their own metrics, but only when drilling
  // into a specific factor (not aggregate/total views).
  const withFactorExposure = (gatherers: unknown[]) =>
    options.selectedFactor
      ? [...createExposureDrilldownGatherers({ ...options, selectedMetricDisplay: null }), ...gatherers]
      : gatherers

  switch (view) {
    case "exposure":
      return createExposureDrilldownGatherers(options)
    case "risk":
      return withFactorExposure(createRiskDrilldownGatherers(options))
    case "performance":
      return withFactorExposure(createPerformanceDrilldownGatherers(options))
    case "beta":
      return createBetaDrilldownGatherers(options)
    case "factor-sensitivity":
    case "predictedFactorSensitivity":
      return createFactorSensitivityDrilldownGatherers({ ...options, factorSensitivityType: FACTOR_SENSITIVITY_TYPES.PREDICTED })
    case "historicalFactorSensitivity":
      return createFactorSensitivityDrilldownGatherers({ ...options, factorSensitivityType: FACTOR_SENSITIVITY_TYPES.HISTORICAL })
    case "composition":
      // Every column composition needs (marketValue, percentEquity, sector, averagePercentEquity) is already in the base gatherers.
      return createDrilldownBaseGatherers(options)
    default:
      // Content-set view — the view ID is the content-set ID.
      return createContentSetDrilldownGatherers({ ...options, contentSetId: view })
  }
}

export interface CreateDrilldownGatherersOptions {
  refreshKey?: number
  groupBy?: unknown
  classifications?: unknown[]
  isRfrAttributionEnabled?: boolean
  betaFields?: unknown
  contentSetMetricDisplay?: string | null
  currentIntradayBatchId?: string | null
  lastIntradayBatchId?: string | null
  isIntradayView?: boolean
}

/**
 * Dispatches to the appropriate view-specific gatherer factory for each
 * instrument present in the frozen snapshot (original + optional comparison).
 */
export function createDrilldownGatherers(
  view: string,
  frozenSnapshot: DrilldownFrozenSnapshot,
  seed: DrilldownSeed,
  options: CreateDrilldownGatherersOptions = {},
) {
  const {
    refreshKey = 0,
    groupBy,
    classifications,
    isRfrAttributionEnabled = false,
    betaFields,
    contentSetMetricDisplay = null,
    currentIntradayBatchId = null,
    lastIntradayBatchId = null,
    isIntradayView = false,
  } = options
  const { modelId, base, startDate, segment, filterScope } = frozenSnapshot
  const { selectedFactor, selectedCategory, selectedMetricDisplay } = seed
  const isContentSetView = !NAMED_VIEW_LABELS[view]
  const baseSegment = computeScopedBaseSegment(segment, parseFilterScope(filterScope))
  const baseWithSegment = base && baseSegment ? { ...base, segment: baseSegment } : base

  const sharedOptions = {
    modelId,
    base: baseWithSegment,
    groupByOptions: groupBy ?? undefined,
    notHeldRollup: true,
    startDate,
    selectedFactor,
    selectedCategory,
    classifications,
    isRfrAttributionEnabled,
    // Performance/content-set need a scalar metricDisplay or the contributor tree stores objects
    // and every attribution cell renders blank. Performance matches the source's % vs $ (default %).
    ...(view === "performance" && { selectedMetricDisplay: selectedMetricDisplay ?? METRIC_TYPES.CUMULATIVE_PERCENT }),
    ...(isContentSetView && contentSetMetricDisplay != null && { selectedMetricDisplay: contentSetMetricDisplay }),
    // Beta interpolates betaFields into its GraphQL selection; without it the query errors out.
    ...(view === "beta" && { betaFields }),
    ...(view === "risk" && { attributionMethod: computeAttributionMethod(Boolean(base)) }),
  }

  // One gatherer set per resolved series — each gathered as-of its own date and under its own
  // suffix so the columns don't collide.
  const { series } = resolveDrilldownInstruments(frozenSnapshot)

  const gatherers = series.flatMap((instrumentSeries) => {
    const isOriginalSeries = instrumentSeries.instrumentType === "original"

    // Intraday routing applies to the ORIGINAL series only; the rebalanced/comparison second series
    // stays EOD on purpose — the frozen snapshot doesn't carry the end-vs-start same-day rules that
    // gate second-instrument intraday, and combined intraday modes are out of scope here. For the
    // original series: every view's gatherer accepts a nullable batchId (intradaySimulationBatch for
    // most views, intradaySimulation for performance), so a live/unpinned session (batchId: null)
    // routes intraday same as a pinned one. Performance mirrors the source panel's gate: a frozen
    // batch still matching the latest committed batch routes to intradaySimulation, while a
    // superseded (genuinely pinned) batch drops to the standard EOD query, which op-hops compounds
    // the committed batch into so benchmark base and not-held contributors line up — that fallback
    // lives inside performance's own gatherer, not here.
    const intradayOptions = isOriginalSeries && isIntradayView
      ? {
        shouldUseIntradayQuery: true,
        on: instrumentSeries.asOf,
        batchId: currentIntradayBatchId,
        lastIntradayBatchId,
      }
      : {}

    return createGatherersForInstrument(view, {
      ...sharedOptions,
      ...intradayOptions,
      // composition/holdings/exposure query `to: endDate`; using this series' as-of date is how the
      // end-vs-start start column gets distinct start-date data.
      endDate: instrumentSeries.asOf,
      positionSet: instrumentSeries.positionSet,
      instrumentSuffix: instrumentSeries.suffix,
    })
  })

  if (refreshKey <= 0) {
    return gatherers
  }
  // Salt every query key with the refresh generation — a refresh must force a refetch, and
  // nothing else in the keys changes when only pricing moved (the performance key carries no
  // batchId at all). Keys stay unsalted until the first refresh so the initial load still
  // dedups against the dashboard's cached queries.
  return gatherers.map((gatherer) => (
    hasSaltableKey(gatherer)
      ? {
        ...gatherer,
        key: [...gatherer.key, `refresh:${refreshKey}`],
        ...(Array.isArray(gatherer.graphqlKey) && { graphqlKey: [...gatherer.graphqlKey, `refresh:${refreshKey}`] }),
      }
      : gatherer
  ))
}

function hasSaltableKey(gatherer: unknown): gatherer is Record<string, unknown> & { key: string[], graphqlKey?: unknown } {
  return typeof gatherer === "object" && gatherer !== null && Array.isArray((gatherer as Record<string, unknown>).key)
}
